import {
	registerMixpanelExitBeacon,
	useMixpanel
} from '~/composables/useMixpanel';
import {
	MixpanelAutoPageviewUserStatusValues,
	MixpanelProps
} from '~/core/service/mixpanel';
import { getMixpanelCampaignParams } from '~/core/service/mixpanel/utils';
import { isBotUserAgent } from '~/utils/isBotUserAgent';
import { SHORTS_BUFFER_SUSTAINED_EVENT } from '~/utils/shortsEvents';

/**
 * Mixpanel Plugin - Initializes Mixpanel analytics on client-side
 *
 * Uses requestIdleCallback to defer Mixpanel loading until browser is idle.
 * This ensures zero impact on LCP, FCP, and TBT.
 */
export default defineNuxtPlugin({
	name: 'mixpanel',
	setup() {
		const runtimeConfig = useRuntimeConfig();
		const mixpanelEnv = runtimeConfig.public.mixpanel;
		const token = mixpanelEnv.token as string;

		// Skip if no token is configured
		if (!token || token.trim() === '') {
			return;
		}

		// Skip for known crawlers. Mixpanel's built-in blocklist
		// (isBlockedUA) misses HeadlessChrome, semrush, AI crawlers and
		// generic tools — this closes that gap before the SDK even loads.
		if (isBotUserAgent()) {
			return;
		}

		// Snapshot the landing URL's UTM params NOW, synchronously. Mixpanel
		// init is deferred to browser idle (below), by which point an SPA
		// navigation may have replaced window.location — so the SDK's own
		// campaign capture would read a clean URL and drop the source
		// ("utm_source (not set)"). We register this snapshot on init instead.
		const landingCampaignParams =
			typeof window !== 'undefined'
				? getMixpanelCampaignParams(window.location.search)
				: {};

		// Defer Mixpanel initialization to idle time
		const initializeMixpanel = async () => {
			try {
				// Dynamic import to avoid bundling in main chunk
				const [{ mixpanelService }, { version }] = await Promise.all([
					import('~/core/service/mixpanel/mixpanel'),
					import('~~/package.json')
				]);

				const MIXPANEL_INSTANCE_KEY = 'mixpanel-instance';
				const debug = mixpanelEnv.debug as boolean;
				const proxyUrl = (mixpanelEnv.proxy_url as string) || undefined;
				const autoPageviewBlockedUrls = [
					/\/imovel\/[^/?#]+(?:\/[^/?#]+)?(?:[?#]|$)/,
					/\/shorts\/[^/?#]+(?:[?#]|$)/,
					/:\/\/[^/]+\/(?:[?#]|$)/, // Home
					/\/(venda|aluguel)\/imoveis/ // SRP
				];

				const { user, loggedIn } = useUserSession();
				const currentUrl = window.location.href;
				const isCurrentUrlAlreadyBlocked = autoPageviewBlockedUrls.some(regex =>
					regex.test(currentUrl)
				);
				const escapedCurrentUrl = currentUrl.replace(
					/[.*+?^${}()|[\]\\]/g,
					'\\$&'
				);
				const initialPageviewBlockRegex = new RegExp(`^${escapedCurrentUrl}$`);

				if (!isCurrentUrlAlreadyBlocked) {
					autoPageviewBlockedUrls.push(initialPageviewBlockRegex);
				}

				const mixpanelConfig = {
					track_pageview: false,
					autocapture: {
						pageview: true,
						click: false,
						dead_click: false,
						input: false,
						scroll: false,
						submit: false,
						rage_click: false,
						block_url_regexes: autoPageviewBlockedUrls
					},
					debug,
					api_host: proxyUrl
				};

				const mixpanel = mixpanelService(
					token,
					mixpanelConfig,
					MIXPANEL_INSTANCE_KEY,
					version
				);

				await mixpanel.init();
				mixpanel.config(user.value);

				// Register the landing UTMs captured synchronously in setup, so
				// the source survives the idle-time init delay and attaches to
				// every event (including the lead), regardless of any navigation
				// that happened while init was pending.
				if (Object.keys(landingCampaignParams).length > 0) {
					mixpanel.register(landingCampaignParams);
				}

				const registerUserStatus = () => {
					mixpanel.register({
						[MixpanelProps.USER_STATUS]: loggedIn.value
							? MixpanelAutoPageviewUserStatusValues.LOGGED_IN
							: MixpanelAutoPageviewUserStatusValues.LOGGED_OUT
					});
				};

				registerUserStatus();
				watch(loggedIn, registerUserStatus);

				if (!isCurrentUrlAlreadyBlocked) {
					// $mp_autocapture mirrors what the SDK's own autocapture sends
					// (mixpanel-browser's Autocapture DEFAULT_PROPS) so this manual
					// track is indistinguishable from a real Page_View [Auto].
					mixpanel.trackPageview({ $mp_autocapture: true });
					autoPageviewBlockedUrls.splice(
						autoPageviewBlockedUrls.indexOf(initialPageviewBlockRegex),
						1
					);
				}

				// Store the instance for later access
				const nuxtApp = useNuxtApp();
				nuxtApp.provide('mixpanel', mixpanel);
				useMixpanel().flushQueue();
			} catch (error) {
				console.error('Failed to initialize Mixpanel:', error);
			}
		};

		// Wait for browser idle time before loading Mixpanel. On SHORTS entry
		// pages, idle CPU arrives long before idle NETWORK — the ~130KB chunk
		// would still compete with the entry video on slow pipes. There the
		// trigger is the video's first frame, the first interaction or a 5s
		// cap, mirroring the GTM loader; events fired meanwhile sit in
		// useMixpanel's queue, and an exit before init flushes that queue via
		// sendBeacon (registered below).
		if (typeof window !== 'undefined') {
			const isShortsEntry = useRoute().path.startsWith('/shorts');
			if (isShortsEntry) {
				let settled = false;
				const cleanups: Array<() => void> = [];
				const fire = () => {
					if (settled) return;
					settled = true;
					for (const undo of cleanups) undo();
					void initializeMixpanel();
				};
				const timer = setTimeout(fire, 15000);
				cleanups.push(() => clearTimeout(timer));
				window.addEventListener(SHORTS_BUFFER_SUSTAINED_EVENT, fire, {
					once: true
				});
				cleanups.push(() =>
					window.removeEventListener(SHORTS_BUFFER_SUSTAINED_EVENT, fire)
				);
				for (const type of ['pointerdown', 'keydown', 'wheel'] as const) {
					window.addEventListener(type, fire, { once: true, passive: true });
					cleanups.push(() => window.removeEventListener(type, fire));
				}
				registerMixpanelExitBeacon(
					token,
					(mixpanelEnv.proxy_url as string) || undefined
				);
			} else if ('requestIdleCallback' in window) {
				requestIdleCallback(() => initializeMixpanel(), { timeout: 5000 });
			} else {
				// Fallback for browsers without requestIdleCallback
				setTimeout(initializeMixpanel, 2500);
			}
		}
	}
});
